// src/pages/Dashboard/Admin/Payments/components/PaymentEditDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaCreditCard, FaSave, FaTimes } from "react-icons/fa";

import { getArticles } from "../../../../../services/articlesServices";
import { getAllUsers } from "../../../../../services/userServices";
import {
  updateReglement,
  createReglement,
} from "../../../../../services/achatsServices";

import {
  PAYMENT_STATUS,
  PAYMENT_TYPES,
  formatDateFR,
  formatEuro,
  safeArray,
  todayISO,
  toAmount,
} from "../utils/paymentsDashboardUtils";

function DrawerField({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function AmountCard({ label, value, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950",
    ok: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10",
  };

  return (
    <div className={`rounded-[22px] border p-4 ${tones[tone] || tones.base}`}>
      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-2 text-xl font-bold text-slate-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}

function isDepositArticle(article) {
  return /acompte|arrhes|d[ée]p[ôo]t/i.test(article?.nom || "");
}

export default function PaymentEditDrawer({ isOpen, payment, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);

  const [formData, setFormData] = useState({
    user_achat_id: "",
    article_id: "",
    quantite: 1,
    date_achat: "",
    message_achat: "",
    statut_reglement: "",
    type_reglement: "",
    type_achat: "",
  });

  const [useDeposit, setUseDeposit] = useState(false);
  const [deposit, setDeposit] = useState({
    montant: "",
    date: todayISO(),
    method: "Espèces",
    status: "Réglé",
    note: "",
    articleId: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);

        const [usersData, articlesData] = await Promise.all([
          getAllUsers(),
          getArticles(),
        ]);

        if (!mounted) return;

        setUsers(safeArray(usersData));
        setArticles(safeArray(articlesData));
      } catch (error) {
        console.error("Erreur chargement drawer édition :", error);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !payment) return;

    setFormData({
      user_achat_id: payment.user_achat_id ?? "",
      article_id: payment.article_id ?? "",
      quantite: payment.quantite ?? 1,
      date_achat: (payment.date_achat || "").slice(0, 10),
      message_achat: payment.message_achat ?? "",
      statut_reglement: payment.statut_reglement ?? "",
      type_reglement: payment.type_reglement ?? "",
      type_achat: payment.type_achat ?? "",
    });

    setUseDeposit(false);
    setDeposit({
      montant: "",
      date: todayISO(),
      method: "Espèces",
      status: "Réglé",
      note: "",
      articleId: "",
    });
  }, [isOpen, payment]);

  const depositCandidates = useMemo(() => {
    return articles.filter(isDepositArticle).map((article) => ({
      value: article.id,
      label: `${article.nom} (${formatEuro(article.prix)})`,
      unit: toAmount(article.prix),
    }));
  }, [articles]);

  useEffect(() => {
    if (!isOpen) return;

    setDeposit((prev) => ({
      ...prev,
      articleId: prev.articleId || depositCandidates?.[0]?.value || "",
    }));
  }, [depositCandidates, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEsc = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose, submitting]);

  const isLicencePayment =
    formData.type_achat === "licence-assurance-cotisation" || !payment?.Article;

  const currentTotal = toAmount(payment?.prix_total);
  const depositAmount = toAmount(deposit.montant);

  const estimatedNewTotal = useMemo(() => {
    if (!useDeposit || depositAmount <= 0) return currentTotal;
    return Math.max(0, Number((currentTotal - depositAmount).toFixed(2)));
  }, [useDeposit, depositAmount, currentTotal]);

  const selectedArticle = useMemo(() => {
    return articles.find(
      (article) => String(article.id) === String(formData.article_id)
    );
  }, [articles, formData.article_id]);

  const linePreviewTotal = useMemo(() => {
    if (isLicencePayment) return currentTotal;

    return toAmount(selectedArticle?.prix) * (Number(formData.quantite) || 0);
  }, [isLicencePayment, currentTotal, selectedArticle, formData.quantite]);

  const closeDrawer = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "quantite") {
      setFormData((prev) => ({
        ...prev,
        quantite: Math.max(1, Number(value || 1)),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!payment?.id) {
      toast.error("Paiement introuvable.");
      return;
    }

    if (!isLicencePayment && !formData.article_id) {
      toast.warn("Veuillez sélectionner un article.");
      return;
    }

    if (useDeposit) {
      if (depositAmount <= 0) {
        toast.warn("Montant d’acompte invalide.");
        return;
      }

      if (depositAmount > currentTotal + 0.009) {
        toast.warn("Le montant d’acompte dépasse le total du règlement.");
        return;
      }

      if (!deposit.articleId) {
        toast.error("Aucun article d’acompte disponible.");
        return;
      }

      const ref = depositCandidates.find(
        (candidate) => String(candidate.value) === String(deposit.articleId)
      );

      if (!ref) {
        toast.error("Article d’acompte introuvable.");
        return;
      }

      if (ref.unit === 0) {
        toast.error("L’article d’acompte a un prix unitaire nul.");
        return;
      }
    }

    try {
      setSubmitting(true);

      if (useDeposit) {
        const ref = depositCandidates.find(
          (candidate) => String(candidate.value) === String(deposit.articleId)
        );

        const quantity = Number((depositAmount / ref.unit).toFixed(4));

        await createReglement({
          user_achat_id: formData.user_achat_id,
          article_id: deposit.articleId,
          quantite: quantity,
          date_achat: deposit.date,
          message_achat: deposit.note
            ? `Acompte sur règlement #${payment.id} — ${deposit.note}`
            : `Acompte sur règlement #${payment.id}`,
          statut_reglement: deposit.status || "Réglé",
          type_reglement: deposit.method || "Espèces",
          type_achat: "boutique",
        });
      }

      const payload = { ...formData };

      if (useDeposit && depositAmount > 0) {
        payload.prix_total = estimatedNewTotal;
        payload.statut_reglement = "Non Réglé";

        const appended = `Acompte de ${formatEuro(depositAmount)} en ${
          deposit.method
        } reçu le ${formatDateFR(deposit.date)}`;

        const baseMessage = String(payload.message_achat || "").trim();

        payload.message_achat = baseMessage
          ? baseMessage.includes(appended)
            ? baseMessage
            : `${baseMessage} — ${appended}`
          : appended;
      }

      await updateReglement(payment.id, payload);

      toast.success(
        useDeposit
          ? "Acompte enregistré et paiement mis à jour."
          : "Paiement mis à jour avec succès."
      );

      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur édition paiement :", error);
      toast.error("Erreur lors de l’enregistrement du paiement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !payment) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={closeDrawer}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification paiement
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaCreditCard className="text-indigo-600 dark:text-indigo-300" />
              Modifier le règlement
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajustez le statut, le moyen de règlement, le message utilisateur
              ou enregistrez un acompte sur ce paiement.
            </p>
          </div>

          <button
            type="button"
            onClick={closeDrawer}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
              event.preventDefault();
            }
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <AmountCard
                label="Total actuel"
                value={formatEuro(currentTotal)}
                tone="base"
              />

              <AmountCard
                label="Acompte saisi"
                value={
                  useDeposit && depositAmount > 0
                    ? `- ${formatEuro(depositAmount)}`
                    : "—"
                }
                tone="warn"
              />

              <AmountCard
                label="Nouveau total"
                value={formatEuro(estimatedNewTotal)}
                tone="ok"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <DrawerField label="Utilisateur *">
                <select
                  name="user_achat_id"
                  value={formData.user_achat_id}
                  onChange={handleChange}
                  required
                  disabled={loading || submitting}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionnez un utilisateur —</option>

                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.nom} {user.prenom}
                    </option>
                  ))}
                </select>
              </DrawerField>

              <DrawerField label="Date d’achat *">
                <input
                  type="date"
                  name="date_achat"
                  value={formData.date_achat}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                />
              </DrawerField>
            </div>

            {!isLicencePayment ? (
              <DrawerField label="Article *">
                <select
                  name="article_id"
                  value={formData.article_id}
                  onChange={handleChange}
                  required
                  disabled={loading || submitting}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionnez un article —</option>

                  {articles.map((article) => (
                    <option key={article.id} value={article.id}>
                      {article.nom}
                    </option>
                  ))}
                </select>
              </DrawerField>
            ) : (
              <div className="rounded-[22px] border border-violet-200 bg-violet-50 p-4 text-sm leading-6 text-violet-800 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
                Ce paiement est un règlement global de type
                licence-cotisation-réduction. L’article principal n’est donc pas
                modifiable comme une ligne boutique simple.
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              <DrawerField label="Quantité *">
                <input
                  type="number"
                  name="quantite"
                  min="1"
                  value={formData.quantite}
                  onChange={handleChange}
                  required
                  disabled={isLicencePayment}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:disabled:bg-slate-800"
                />
              </DrawerField>

              <DrawerField label="Statut *">
                <select
                  name="statut_reglement"
                  value={formData.statut_reglement}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionnez un statut —</option>

                  {PAYMENT_STATUS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </DrawerField>

              <DrawerField label="Type de règlement *">
                <select
                  name="type_reglement"
                  value={formData.type_reglement}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">— Sélectionnez un type —</option>

                  {PAYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </DrawerField>
            </div>

            {!isLicencePayment ? (
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      Aperçu article
                    </p>

                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {selectedArticle
                        ? `${selectedArticle.nom} · ${formatEuro(
                            selectedArticle.prix
                          )} × ${formData.quantite}`
                        : "Aucun article sélectionné"}
                    </p>
                  </div>

                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {formatEuro(linePreviewTotal)}
                  </p>
                </div>
              </div>
            ) : null}

            <DrawerField
              label="Message utilisateur"
              hint="Le message peut préciser un acompte, une remise, une information de taille, ou une consigne de paiement."
            >
              <textarea
                name="message_achat"
                value={formData.message_achat}
                onChange={handleChange}
                rows={5}
                placeholder="Message visible par l’utilisateur..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Acompte
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Enregistre une ligne d’acompte séparée, puis diminue le
                    montant restant dû du règlement actuel.
                  </p>
                </div>

                <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={useDeposit}
                    onChange={(event) => setUseDeposit(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  Activer
                </label>
              </div>

              {useDeposit ? (
                <div className="mt-4 space-y-4">
                  {depositCandidates.length === 0 ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm leading-6 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
                      Aucun article “Acompte” trouvé. Crée d’abord un article
                      d’acompte dans le catalogue boutique.
                    </div>
                  ) : null}

                  <div className="grid gap-4 lg:grid-cols-2">
                    <DrawerField label="Article d’acompte *">
                      <select
                        value={deposit.articleId}
                        onChange={(event) =>
                          setDeposit((prev) => ({
                            ...prev,
                            articleId: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      >
                        {depositCandidates.map((candidate) => (
                          <option key={candidate.value} value={candidate.value}>
                            {candidate.label}
                          </option>
                        ))}
                      </select>
                    </DrawerField>

                    <DrawerField label="Montant de l’acompte (€) *">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={deposit.montant}
                        onChange={(event) =>
                          setDeposit((prev) => ({
                            ...prev,
                            montant: event.target.value,
                          }))
                        }
                        placeholder="Ex : 50"
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <DrawerField label="Date acompte *">
                      <input
                        type="date"
                        value={deposit.date}
                        onChange={(event) =>
                          setDeposit((prev) => ({
                            ...prev,
                            date: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="Type acompte *">
                      <select
                        value={deposit.method}
                        onChange={(event) =>
                          setDeposit((prev) => ({
                            ...prev,
                            method: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      >
                        {PAYMENT_TYPES.filter((type) => type !== "En ligne").map(
                          (type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          )
                        )}
                      </select>
                    </DrawerField>

                    <DrawerField label="Statut acompte *">
                      <select
                        value={deposit.status}
                        onChange={(event) =>
                          setDeposit((prev) => ({
                            ...prev,
                            status: event.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      >
                        {["Réglé", "En Attente", "Annulé"].map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </DrawerField>
                  </div>

                  <DrawerField label="Note acompte">
                    <input
                      type="text"
                      value={deposit.note}
                      onChange={(event) =>
                        setDeposit((prev) => ({
                          ...prev,
                          note: event.target.value,
                        }))
                      }
                      placeholder="Ex : reçu pendant l’inscription, chèque n°..."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </DrawerField>
                </div>
              ) : null}
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDrawer}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={submitting || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}